'use client';

import { useCallback, useState } from 'react';
import { useContextSelector } from 'use-context-selector';

import { contactBusiness } from '@/core/apis/asset-manager-apis';
import { authSelector } from '@/core/store/global/app-states';
import { AppContext } from '@/core/store/global/AppProvider';

import { useAmbientIntelligence } from './useAmbientIntelligence';

interface OpenMessageOptions {
  vendorId?: string;
  vendorName?: string;
  prefilledMessage?: string;
  contentContext?: any;
}

export interface PendingMessageState extends OpenMessageOptions {
  isProcessing?: boolean;
}

export const useOpenMessage = () => {
  const auth = useContextSelector(AppContext, authSelector);
  const { actions, state } = useAmbientIntelligence();
  const [isProcessing, setIsProcessing] = useState(false);

  const openMessage = useCallback(
    async (options: OpenMessageOptions) => {
      // Always switch to communication mode first for seamless experience
      actions.switchMode('communication');
      actions.setShowCards(true);

      // If user is not authenticated, store pending message for after auth
      if (!auth?.loggedIn) {
        // Store pending message options in state
        actions.setPendingMessage?.(options);
        actions.setShowGuestLogin(true);
        return;
      }

      // User is authenticated, proceed with opening message
      setIsProcessing(true);

      try {
        let threadId: string | null = null;

        // If vendor ID is provided, create/get thread with vendor
        if (options.vendorId) {
          const threadResponse = await contactBusiness(options.vendorId);

          // Check different possible response structures
          if (threadResponse?.data?.threadId) {
            threadId = threadResponse.data.threadId;
          } else if (threadResponse?.threadId) {
            threadId = threadResponse.threadId;
          } else if (threadResponse?._id) {
            // Sometimes the response might be the thread object itself
            threadId = threadResponse._id;
          } else if (typeof threadResponse === 'string') {
            // Sometimes it might just return the ID as a string
            threadId = threadResponse;
          }
        }

        // Store prefilled message in draft if provided
        if (threadId && options.prefilledMessage) {
          actions.saveDraftMessage(threadId, options.prefilledMessage);

          // Give a small delay to ensure draft is saved to state
          await new Promise((resolve) => setTimeout(resolve, 50));
        }

        // Set the active thread after ensuring draft is saved
        if (threadId) {
          actions.setActiveThread(threadId);
        }
      } catch (error) {
        console.error('Failed to open message:', error);
      } finally {
        setIsProcessing(false);
      }
    },
    [auth, actions],
  );

  // Process pending message after auth success
  const processPendingMessage = useCallback(
    async (pendingMessage: OpenMessageOptions) => {
      setIsProcessing(true);

      try {
        let threadId: string | null = null;

        if (pendingMessage.vendorId) {
          const threadResponse = await contactBusiness(pendingMessage.vendorId);

          // Check different possible response structures
          if (threadResponse?.data?.threadId) {
            threadId = threadResponse.data.threadId;
          } else if (threadResponse?.threadId) {
            threadId = threadResponse.threadId;
          } else if (threadResponse?._id) {
            threadId = threadResponse._id;
          } else if (typeof threadResponse === 'string') {
            threadId = threadResponse;
          }
        }

        if (threadId && pendingMessage.prefilledMessage) {
          actions.saveDraftMessage(threadId, pendingMessage.prefilledMessage);

          // Give a small delay to ensure draft is saved to state
          await new Promise((resolve) => setTimeout(resolve, 50));
        }

        if (threadId) {
          actions.setActiveThread(threadId);
        }
      } catch (error) {
        console.error('Failed to process pending message:', error);
      } finally {
        setIsProcessing(false);
      }
    },
    [actions],
  );

  return {
    openMessage,
    processPendingMessage,
    isProcessing,
  };
};
